import { useCallback, useMemo, useState } from 'react'
import type { GridColumn, GridTreeData } from './types'
import { defaultComparator } from './useGridState'

/** Everything a row's group cell needs to draw itself in the hierarchy. */
export interface GridTreeMeta {
  depth: number
  hasChildren: boolean
  expanded: boolean
  /** No further siblings below this row — its connector ends in an L, not a T. */
  isLastSibling: boolean
  /**
   * One entry per ancestor level (index 0 = the outermost), true when that
   * branch still has rows below this one. Drives the vertical guide lines: a
   * branch that has ended must not keep drawing a line past its last row.
   */
  ancestorContinues: boolean[]
  /** The row's own path, as `getPath` reported it. */
  path: string[]
  parentId: string | null
}

export interface TreeRowsResult<T> {
  rows: T[]
  metaById: Map<string, GridTreeMeta>
}

interface BuildTreeRowsArgs<T> {
  rows: T[]
  getRowId: (row: T) => string
  getPath: (row: T) => string[]
  /** Applied within each set of siblings, so the hierarchy survives sorting. */
  comparator?: (a: T, b: T) => number
  isExpanded: (rowId: string, row: T) => boolean
}

const PATH_SEPARATOR = ' '

interface TreeNode<T> {
  row: T
  id: string
  path: string[]
  children: TreeNode<T>[]
  parent: TreeNode<T> | null
}

/**
 * Turns a flat row list into tree display order.
 *
 * Rows carry their own path (`getPath`), the way ag-grid's `getDataPath` did:
 * a row whose path is `[a, b]` is a child of the row whose path is `[a]`. A row
 * whose parent is missing from the list is treated as a root, which is what
 * makes a search result — where a matching sub-pipe arrives without its parent —
 * render in place rather than disappear.
 *
 * Sorting happens inside each sibling group only. Sorting the flat list instead
 * would scatter children away from their parents and destroy the tree.
 */
export const buildTreeRows = <T,>({
  rows,
  getRowId,
  getPath,
  comparator,
  isExpanded,
}: BuildTreeRowsArgs<T>): TreeRowsResult<T> => {
  const byPath = new Map<string, TreeNode<T>>()
  const roots: TreeNode<T>[] = []
  const nodes: TreeNode<T>[] = []

  rows.forEach((row) => {
    const path = getPath(row) ?? []
    const node: TreeNode<T> = {
      row,
      id: getRowId(row),
      path,
      children: [],
      parent: null,
    }
    nodes.push(node)
    byPath.set(path.join(PATH_SEPARATOR), node)
  })

  nodes.forEach((node) => {
    if (node.path.length <= 1) {
      roots.push(node)
      return
    }
    const parent = byPath.get(node.path.slice(0, -1).join(PATH_SEPARATOR))
    if (parent) {
      node.parent = parent
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  })

  if (comparator) {
    const sortLevel = (list: TreeNode<T>[]) => {
      list.sort((a, b) => comparator(a.row, b.row))
      list.forEach((node) => sortLevel(node.children))
    }
    sortLevel(roots)
  }

  const out: T[] = []
  const metaById = new Map<string, GridTreeMeta>()

  const walk = (
    list: TreeNode<T>[],
    depth: number,
    ancestorContinues: boolean[],
  ) => {
    list.forEach((node, index) => {
      const isLastSibling = index === list.length - 1
      const hasChildren = node.children.length > 0
      const expanded = hasChildren ? isExpanded(node.id, node.row) : false

      out.push(node.row)
      metaById.set(node.id, {
        depth,
        hasChildren,
        expanded,
        isLastSibling,
        ancestorContinues,
        path: node.path,
        parentId: node.parent?.id ?? null,
      })

      if (hasChildren && expanded) {
        walk(node.children, depth + 1, [...ancestorContinues, !isLastSibling])
      }
    })
  }

  walk(roots, 0, [])

  return { rows: out, metaById }
}

interface UseTreeRowsArgs<T> {
  rows: T[]
  columns: GridColumn<T>[]
  getRowId: (row: T) => string
  treeData?: GridTreeData<T>
  sort: { field: string; desc: boolean } | null
  postSort?: (rows: T[]) => T[]
}

/**
 * Tree row model: flatten, expand/collapse, and sort-within-siblings.
 *
 * Expansion is stored as a default plus per-row overrides rather than a set of
 * expanded ids: `expandAll` / `collapseAll` then cost one state write and, more
 * importantly, rows that arrive later (the next page of pipes) inherit the
 * current default instead of silently collapsing.
 */
export const useTreeRows = <T,>({
  rows,
  columns,
  getRowId,
  treeData,
  sort,
  postSort,
}: UseTreeRowsArgs<T>) => {
  const [expansion, setExpansion] = useState<{
    default: boolean
    overrides: Record<string, boolean>
  }>(() => ({ default: treeData?.defaultExpanded !== false, overrides: {} }))

  const isExpanded = useCallback(
    (rowId: string) => expansion.overrides[rowId] ?? expansion.default,
    [expansion],
  )

  const setExpanded = useCallback((rowId: string, expanded: boolean) => {
    setExpansion((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, [rowId]: expanded },
    }))
  }, [])

  const expandAll = useCallback(() => {
    setExpansion({ default: true, overrides: {} })
  }, [])

  const collapseAll = useCallback(() => {
    setExpansion({ default: false, overrides: {} })
  }, [])

  const comparator = useMemo(() => {
    if (!sort) return undefined
    const column = columns.find((col) => col.field === sort.field)
    const getValue = (row: T) =>
      column?.valueGetter
        ? column.valueGetter(row)
        : (row as Record<string, unknown>)[sort.field]

    return (a: T, b: T) => {
      const result = column?.comparator
        ? column.comparator(getValue(a), getValue(b), a, b)
        : defaultComparator(getValue(a), getValue(b))
      return sort.desc ? -result : result
    }
  }, [sort, columns])

  const { rows: flatRows, metaById } = useMemo(() => {
    if (!treeData) return { rows, metaById: new Map<string, GridTreeMeta>() }
    return buildTreeRows({
      rows,
      getRowId,
      getPath: treeData.getPath,
      comparator,
      isExpanded,
    })
  }, [treeData, rows, getRowId, comparator, isExpanded])

  const displayedRows = useMemo(
    () => (treeData && postSort ? postSort(flatRows) : flatRows),
    [treeData, postSort, flatRows],
  )

  return {
    rows: displayedRows,
    metaById,
    isExpanded,
    setExpanded,
    expandAll,
    collapseAll,
  }
}
